/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import React from 'react';
import { Copy, ExitDomain } from '@opengeoweb/theme';
import type { InfoDialogContactsProps } from '../../store/types';
import { useDataExplorerTranslation } from '../../utils/i18n';

const DiscoveryCatalogueInfo: React.FC<InfoDialogContactsProps> = ({
  addresses,
  contactInstructions,
  emails,
  hoursOfService,
  links,
  name,
  organization,
  phones,
  roles,
}: InfoDialogContactsProps) => {
  const { t } = useDataExplorerTranslation();
  // Add colon between hours and minutes in the format HHMMh
  const formatHoursOfService = (hoursOfService: string): string => {
    return hoursOfService.replace(/(\d{2})(\d{2})h/g, '$1:$2');
  };

  return (
    <Grid
      container
      data-testid="InfoDialogContactsInfo"
      spacing={2}
      sx={{ flexDirection: 'column' }}
    >
      {!!name && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-name')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>{name}</Typography>
          </Grid>
        </Grid>
      )}
      {!!addresses?.[0].deliveryPoint && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-address')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>{addresses[0].deliveryPoint[0]}</Typography>
          </Grid>
        </Grid>
      )}
      {!!emails?.[0].value && (
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            flexDirection: 'row',
          }}
        >
          <Grid
            container
            size={10}
            spacing={0}
            sx={{ flexDirection: 'column' }}
          >
            <Grid>
              <Typography color="textSecondary" variant="caption">
                {t('contacts-email')}
              </Typography>
            </Grid>
            <Grid>
              <Typography>{emails[0].value}</Typography>
            </Grid>
          </Grid>
          <Grid
            container
            size={2}
            sx={{
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}
          >
            <Grid>
              <Tooltip title={t('Copy the link')}>
                <IconButton
                  onClick={() => {
                    navigator.clipboard
                      .writeText(emails?.[0]?.value || '')
                      .catch((error) => {
                        console.error(
                          'Failed to copy text to clipboard:',
                          error,
                        );
                      });
                  }}
                  style={{ borderRadius: 50 }}
                >
                  <Copy />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!!links?.[0].href && (
        <Grid
          container
          direction="row"
          size={12}
          spacing={0}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Grid container sx={{ flexDirection: 'column' }}>
            <Grid>
              <Typography color="textSecondary" variant="caption">
                {t('contacts-web')}
              </Typography>
            </Grid>
            <Grid>
              <Typography>{links[0].href}</Typography>
            </Grid>
          </Grid>
          <Grid
            container
            direction="row"
            sx={{
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
          >
            <MuiLink href={links[0].href} rel="noopener" target="_blank">
              <Tooltip title={t('Open')}>
                <IconButton style={{ borderRadius: 50, color: '#186DFF' }}>
                  <ExitDomain />
                </IconButton>
              </Tooltip>
            </MuiLink>
            <Grid>
              <Tooltip title={t('Copy the link')}>
                <IconButton
                  onClick={() => {
                    navigator.clipboard
                      .writeText(links?.[0]?.href || '')
                      .catch((error) => {
                        console.error(
                          'Failed to copy text to clipboard:',
                          error,
                        );
                      });
                  }}
                  style={{ borderRadius: 50 }}
                >
                  <Copy />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!!roles && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-role')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>
              {roles[0].charAt(0).toUpperCase() + roles[0].slice(1)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {!!organization && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-organisation')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>{organization}</Typography>
          </Grid>
        </Grid>
      )}
      {!!hoursOfService && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-hours-of-service')}
            </Typography>
          </Grid>
          <Grid>
            <Typography data-testid="contactsHoursOfService">
              {formatHoursOfService(hoursOfService)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {!!contactInstructions && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-contact-instructions')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>
              {contactInstructions.charAt(0).toUpperCase() +
                contactInstructions.slice(1)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {!!phones?.[0].value && (
        <Grid container spacing={0} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary" variant="caption">
              {t('contacts-telephone')}
            </Typography>
          </Grid>
          <Grid>
            <Typography>{phones[0].value}</Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default DiscoveryCatalogueInfo;
